<template>
  <Transition name="modal">
    <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-dvh items-center justify-center p-4">
        <Transition name="modal-backdrop">
          <div v-if="show" class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="closeOnBackdrop && $emit('close')"></div>
        </Transition>

        <Transition name="modal-content">
          <div
            v-if="show"
            ref="panelRef"
            :class="modalClasses"
            class="relative bg-white dark:bg-neutral-800 rounded-xl shadow-large w-full z-10 focus:outline-none"
            :role="role"
            aria-modal="true"
            :aria-labelledby="hasHeader ? titleId : null"
            :aria-label="!hasHeader && ariaLabel ? ariaLabel : null"
            :aria-describedby="ariaDescribedby || null"
            tabindex="-1"
            @click.stop
          >
            <div v-if="$slots.header || title" class="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <div :id="titleId">
                <slot name="header">
                  <h3 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100">{{ title }}</h3>
                </slot>
              </div>
              <button
                v-if="closable"
                type="button"
                aria-label="Close dialog"
                @click="$emit('close')"
                class="p-1.5 -m-1.5 rounded-lg text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <div class="px-6 py-4">
              <slot></slot>
            </div>

            <div v-if="$slots.footer" class="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-b-xl">
              <slot name="footer"></slot>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script>
import { computed, ref, watch, nextTick, onUnmounted } from 'vue'

// Module-level state so stacked modals coordinate: unique title ids, a single
// body scroll lock (counter, not boolean) and a stack deciding which modal is
// topmost (only it responds to Escape / traps Tab).
let uidCounter = 0
let bodyLockCount = 0
const openStack = []

const lockBodyScroll = () => {
  if (++bodyLockCount === 1) document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  if (bodyLockCount > 0 && --bodyLockCount === 0) document.body.style.overflow = ''
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default {
  name: 'Modal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    closable: {
      type: Boolean,
      default: true
    },
    // Default false: every existing consumer is a form modal authored while
    // backdrop-close was broken — a stray click must not discard their input.
    // Read-only/confirm modals opt in explicitly.
    closeOnBackdrop: {
      type: Boolean,
      default: false
    },
    role: {
      type: String,
      default: 'dialog',
      validator: (value) => ['dialog', 'alertdialog'].includes(value)
    },
    // Accessible name when no title/header is rendered.
    ariaLabel: {
      type: String,
      default: ''
    },
    // Optional id of a description node (e.g. ConfirmDialog's message).
    ariaDescribedby: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  setup(props, { slots, emit }) {
    const uid = ++uidCounter
    const titleId = `modal-title-${uid}`
    const panelRef = ref(null)
    let previouslyFocused = null
    let isOpen = false

    const hasHeader = computed(() => !!(slots.header || props.title))

    const modalClasses = computed(() => {
      const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-full mx-4'
      }
      return sizeClasses[props.size]
    })

    const getFocusables = () => {
      if (!panelRef.value) return []
      return Array.from(panelRef.value.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => el.offsetParent !== null)
    }

    const onKeydown = (e) => {
      if (openStack[openStack.length - 1] !== uid) return

      if (e.key === 'Escape') {
        // Nested widgets may consume Escape for their own dismissal. vue-select
        // closes its dropdown without preventDefault, so also check for an
        // open .vs--open ancestor before closing the dialog under it.
        if (e.defaultPrevented) return
        if (e.target && e.target.closest && e.target.closest('.vs--open')) return
        if (props.closable) {
          e.preventDefault()
          emit('close')
        }
        return
      }

      if (e.key !== 'Tab') return

      const focusables = getFocusables()
      if (focusables.length === 0) {
        e.preventDefault()
        if (panelRef.value) panelRef.value.focus()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      const inside = panelRef.value && panelRef.value.contains(active)
      if (e.shiftKey) {
        if (!inside || active === first || active === panelRef.value) {
          e.preventDefault()
          last.focus()
        }
      } else if (!inside || active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    const onOpen = async () => {
      if (isOpen) return
      isOpen = true
      previouslyFocused = document.activeElement
      openStack.push(uid)
      lockBodyScroll()
      document.addEventListener('keydown', onKeydown)
      await nextTick()
      // Focus the panel, not the first focusable — otherwise every titled
      // modal opens with a focus ring on the header X button.
      if (panelRef.value) panelRef.value.focus()
    }

    const onClose = () => {
      if (!isOpen) return
      isOpen = false
      document.removeEventListener('keydown', onKeydown)
      const index = openStack.indexOf(uid)
      if (index !== -1) openStack.splice(index, 1)
      unlockBodyScroll()
      if (
        previouslyFocused &&
        document.contains(previouslyFocused) &&
        typeof previouslyFocused.focus === 'function'
      ) {
        previouslyFocused.focus()
      }
      previouslyFocused = null
    }

    watch(
      () => props.show,
      (newValue) => {
        if (newValue) {
          onOpen()
        } else {
          onClose()
        }
      },
      { immediate: true }
    )

    // The watcher never fires if the component unmounts while open (route
    // change, parent v-if) — without this the scroll-lock counter leaks.
    onUnmounted(onClose)

    return {
      modalClasses,
      hasHeader,
      titleId,
      panelRef
    }
  }
}
</script>

<style scoped>
.modal-enter-active {
  transition: opacity 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-backdrop-enter-active {
  transition: opacity 0.2s ease-out;
}

.modal-backdrop-leave-active {
  transition: opacity 0.15s ease-in;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-content-enter-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}

.modal-content-leave-active {
  transition: opacity 0.15s ease-in, transform 0.15s ease-in;
}

.modal-content-enter-from,
.modal-content-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

@media (prefers-reduced-motion: reduce) {
  .modal-enter-active,
  .modal-leave-active,
  .modal-backdrop-enter-active,
  .modal-backdrop-leave-active,
  .modal-content-enter-active,
  .modal-content-leave-active {
    transition: none;
  }
}
</style>
